import { useEffect, useReducer, useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isEqual, cloneDeep, isEmpty } from 'lodash'
import moment from 'moment'

import {
	Typography,
	Autocomplete,
	Box,
	TextField,
	Grid,
	Select,
	MenuItem,
	Button,
	Chip,
	FormControl,
} from '@mui/material'
import { makeStyles } from '@mui/styles'
import { DeleteOutline as DeleteIcon } from '@mui/icons-material'
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import ColorPicker, { PopUpColorPicker } from 'custom-components/ColorPicker/ColorPicker'

import { toDayjs } from 'utils/functions/doformsDateUtil'
import { IconThemeContext } from 'custom-components/context/IconThemesContext'
import { JOB_STATUS, JOB_STATUS_INFO, STATUS_ICONS, DEFAULT_COLOR } from './helper/constant'
import ModalDashboardSettingComponent from '../modal-dashboard/ModalDashboardSettingComponent'

import {
	stringFilter,
	numberFilter,
	dateFilter,
	getDefaultOperator,
	numberTypes,
	dateTimeTypes,
	stringTypes,
} from '../../../../../utils/functions/helpers'

import { generateRandomID } from '../../../../../utils/functions/helpers'

const useStyles = makeStyles(() => ({
	closeIconSort: {
		marginRight: '20px',
		marginTop: '12px',
		width: '5%',
	},
	filterRowConType: {
		width: '10%',
	},
	filterRowField: {
		width: '30%',
	},
	filterRowOpe: {
		width: '20%',
	},
	filterRowVal: {
		width: '30%',
	},
	dateBox: {
		marginTop: '16px',
	},
	input: {
		'& .MuiInput-input': {
			fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
			fontWeight: '400',
			fontSize: '1rem',
			lineHeight: '1.4375em',
			letterSpacing: '0.00938em',
		},
	},
}))

const operatorMap = {
	EQ: '=',
	NE: '!=',
}

const WIDGET_ACTION_TYPES = {
	INIT: 'INIT',
	ADD_ITEM: 'ADD_ITEM',
	UPDATE_ITEM: 'UPDATE_ITEM',
	REMOVE_ITEM: 'REMOVE_ITEM',
}

function checkSameOperator(aType, bType) {
	if (numberTypes.includes(aType) && numberTypes.includes(bType)) return true
	if (stringTypes.includes(aType) && stringTypes.includes(bType)) return true
	if (dateTimeTypes.includes(aType) && dateTimeTypes.includes(bType)) return true
	return false
}

function widgetItemsReducer(state, action) {
	switch (action.type) {
		case WIDGET_ACTION_TYPES.INIT:
			return action.payload
		case WIDGET_ACTION_TYPES.ADD_ITEM:
			return [...state, action.payload]
		case WIDGET_ACTION_TYPES.UPDATE_ITEM:
			return state.map((item, index) =>
				index === action.payload.index ? { ...item, ...action.payload.data } : item
			)
		case WIDGET_ACTION_TYPES.REMOVE_ITEM:
			return state.filter((_, index) => index !== action.payload.index)
		default:
			return state
	}
}

function JobSummaryWidgit({
	summaryWidget = [],
	statusField,
	onStatusFieldChange,
	allColumns,
	onWidgetItemChange,
	title,
	hasModalDashboardSetting = false,
	modalDashboardSetting,
	onModalDashboardSettingChange,
}) {
	const { t } = useTranslation('common')

	const [widgetItems, dispatchWigetItems] = useReducer(widgetItemsReducer, summaryWidget)

	useEffect(() => {
		if (isEqual(widgetItems, summaryWidget)) return

		dispatchWigetItems({ type: WIDGET_ACTION_TYPES.INIT, payload: summaryWidget })
	}, [summaryWidget])

	useEffect(() => {
		if (isEqual(widgetItems, summaryWidget)) return

		onWidgetItemChange(widgetItems)
	}, [widgetItems])

	const [selectedStatusColumn, setSelectedStatusColumn] = useState(null)

	useEffect(() => {
		if (isEmpty(allColumns) || !statusField) {
			if (isEqual(selectedStatusColumn, null)) return

			setSelectedStatusColumn(null)
		}

		const statusColumn = allColumns.find((column) => column.name === statusField)
		if (isEqual(selectedStatusColumn, statusColumn)) return

		setSelectedStatusColumn(statusColumn)
	}, [allColumns, statusField])

	const handleAdd = () => {
		const ope = getDefaultOperator(selectedStatusColumn?.type)
		const newItem = {
			label: '',
			icon: undefined,
			operator: ope,
			value: '',
			id: generateRandomID(),
			color: DEFAULT_COLOR,
		}

		dispatchWigetItems({ type: WIDGET_ACTION_TYPES.ADD_ITEM, payload: newItem })
	}

	return (
		<div>
			<Typography variant="subtitle2" sx={{ pl: 1, mb: 1 }}>
				{title}
			</Typography>
			<Box
				sx={{
					width: '100%',
					minHeight: 180,
					border: '1px solid #ddd',
					borderRadius: 1,
					p: 2,
					background: '#fff',
				}}
			>
				<Box sx={{ display: 'grid', background: '#fafafa', mb: 1, px: 0 }}>
					<FormControl variant="outlined" sx={{ mb: '15px' }} size="small" fullWidth>
						<Typography variant="subtitle2" sx={{ pl: 1 }}>
							{t('common:input.statusField')}
						</Typography>
						<Select
							value={statusField || ''}
							onChange={(e) => {
								const newStatus = e.target.value
								onStatusFieldChange(e.target.value)
								const newStatusColumn = allColumns.find((column) => column.name === newStatus)
								if (
									checkSameOperator(newStatusColumn?.type, selectedStatusColumn?.type) ||
									isEmpty(summaryWidget)
								)
									return
								const newWidgetItem = cloneDeep(summaryWidget)?.map((widgetItem) => {
									if (widgetItem.operator === 'total') return widgetItem
									return {
										...widgetItem,
										operator: getDefaultOperator(newStatusColumn?.type),
										value: '',
									}
								})
								onWidgetItemChange(newWidgetItem)
							}}
							size="small"
							fullWidth
						>
							<MenuItem value="">
								<em>None</em>
							</MenuItem>
							{allColumns.map((col) => (
								<MenuItem value={col.name}>{col.title}</MenuItem>
							))}
						</Select>
					</FormControl>
					<Grid spacing={0.5} container>
						<Grid item xs={2} md={2} lg={2} sx={{ fontWeight: 600, textAlign: 'left' }}>
							Label
						</Grid>
						<Grid item xs={1.5} md={1.5} lg={1.5} sx={{ fontWeight: 600, textAlign: 'left' }}>
							Icon
						</Grid>
						<Grid item xs={2.5} md={2.5} lg={2.5} sx={{ fontWeight: 600, textAlign: 'left' }}>
							Color
						</Grid>
						<Grid item xs={4.5} md={4.5} lg={4.5} sx={{ fontWeight: 600, textAlign: 'left' }}>
							Formula
						</Grid>
						<Grid item xs={1.5} md={1.5} lg={1.5} sx={{ fontWeight: 600, textAlign: 'right' }}>
							<Typography
								variant="body2"
								sx={{
									color: 'primary.main',
									fontWeight: 600,
									cursor: 'pointer',
									userSelect: 'none',
									minWidth: 48,
									textAlign: 'right',
								}}
								onClick={handleAdd}
							>
								+ {t('common:misc.add')}
							</Typography>
						</Grid>
					</Grid>
				</Box>
				{widgetItems.map((item, index) => (
					<Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
						<JobSummaryWidgitItem
							index={index}
							value={item}
							dispatchWigetItems={dispatchWigetItems}
							selectedStatusColumn={selectedStatusColumn}
						/>
					</Box>
				))}

				{hasModalDashboardSetting && onModalDashboardSettingChange && (
					<div style={{ marginTop: '15px' }}>
						<ModalDashboardSettingComponent
							setting={modalDashboardSetting}
							onChangeSetting={(value) => {
								onModalDashboardSettingChange(value)
							}}
							dataFields={allColumns}
						/>
					</div>
				)}
			</Box>
		</div>
	)
}

function JobSummaryWidgitItem({ index, value, dispatchWigetItems, selectedStatusColumn }) {
	const handleLabelChange = (e) => {
		dispatchWigetItems({
			type: WIDGET_ACTION_TYPES.UPDATE_ITEM,
			payload: {
				index,
				data: {
					...value,
					label: e.target.value,
				},
			},
		})
	}

	const handleIconChange = (e) => {
		dispatchWigetItems({
			type: WIDGET_ACTION_TYPES.UPDATE_ITEM,
			payload: {
				index,
				data: {
					...value,
					icon: e.target.value,
				},
			},
		})
	}

	// const handleTargetColumnChange = (e) => {
	// 	const selectedColumnName = e.target.value
	// 	const selectedColumn = allColumns.find((col) => col.name === selectedColumnName)
	// 	const newOperator = getDefaultOperator(selectedColumn?.type || '')
	// 	dispatchWigetItems({
	// 		type: WIDGET_ACTION_TYPES.UPDATE_ITEM,
	// 		payload: {
	// 			index,
	// 			data: {
	// 				...value,
	// 				field: selectedColumnName,
	// 				type: selectedColumn?.type || '',
	// 				operator: newOperator,
	// 			},
	// 		},
	// 	})
	// }

	const handleTargetOperatorChange = (e) => {
		const newOperator = e.target.value
		const newItem = cloneDeep(value)
		newItem['operator'] = newOperator
		if (newOperator === 'isAnyOf') {
			newItem['value'] = []
		} else if (newOperator === 'isEmpty' || newOperator === 'isNotEmpty') {
			newItem['value'] = ''
		}
		dispatchWigetItems({
			type: WIDGET_ACTION_TYPES.UPDATE_ITEM,
			payload: {
				index,
				data: {
					...value,
					operator: e.target.value,
				},
			},
		})
	}

	const handleDeleteItem = () => {
		dispatchWigetItems({
			type: WIDGET_ACTION_TYPES.REMOVE_ITEM,
			payload: {
				index,
			},
		})
	}

	const handleChangeValue = (newValue) => {
		if (isEqual(value.value, newValue)) return

		const newItem = cloneDeep(value)
		newItem['value'] =
			value.operator !== 'isAnyOf' ? newValue : Array.isArray(newValue) ? [...newValue] : [newValue]
		dispatchWigetItems({
			type: WIDGET_ACTION_TYPES.UPDATE_ITEM,
			payload: {
				index,
				data: newItem,
			},
		})
	}

	const handleColorChange = (newValue) => {
		if (isEqual(value.color, newValue)) return

		dispatchWigetItems({
			type: WIDGET_ACTION_TYPES.UPDATE_ITEM,
			payload: {
				index,
				data: {
					...value,
					color: newValue,
				},
			},
		})
	}

	return (
		<Grid key={`widget_item_${index}`} spacing={0.5} container>
			<Grid item xs={2} md={2} lg={2} fullWidth>
				<TextField
					sx={{
						fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
						fontWeight: '400',
						lineHeight: '1.4375em',
						letterSpacing: '0.00938em',
					}}
					value={value?.label || ''}
					onChange={handleLabelChange}
					size="small"
					InputProps={{
						sx: {
							height: '40px',
						},
					}}
				/>
			</Grid>
			<Grid item xs={1.5} md={1.5} lg={1.5} fullWidth>
				<Select fullWidth size="small" value={value?.icon || ''} onChange={handleIconChange}>
					{Object.values(JOB_STATUS_INFO).map((info, index) => {
						const IconComponent = STATUS_ICONS[info.id]
						const statusIconProps = {
							style: {
								color: info.iconColor,
							},
						}
						return (
							<MenuItem key={index} value={info.id}>
								<Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
									<IconComponent {...statusIconProps} />
								</Box>
							</MenuItem>
						)
					})}
				</Select>
			</Grid>
			<Grid item xs={2.5} md={2.5} lg={2.5}>
				<PopUpColorPicker
					value={value?.color}
					onColorChange={handleColorChange}
					customStyle={{
						label: {
							display: 'flex',
							alignItems: 'center',
							height: '39px',
						},
						circle: {
							width: '20px',
							height: '20px',
						},
					}}
				/>
			</Grid>
			<Grid item xs={4.5} md={4.5} lg={4.5} fullWidth>
				<Grid container fullWidth spacing={0.5}>
					<Grid item xs={6} md={6} lg={6}>
						<Select
							value={value.operator}
							size="small"
							onChange={handleTargetOperatorChange}
							fullWidth
						>
							{stringTypes.includes(selectedStatusColumn?.type?.toUpperCase()) &&
								Object.keys(stringFilter).map((item2) => {
									return <MenuItem value={item2}>{stringFilter[item2]}</MenuItem>
								})}
							{numberTypes.includes(selectedStatusColumn?.type?.toUpperCase()) &&
								Object.keys(numberFilter).map((item2) => {
									return <MenuItem value={item2}>{numberFilter[item2]}</MenuItem>
								})}
							{dateTimeTypes.includes(selectedStatusColumn?.type?.toUpperCase()) &&
								Object.keys(dateFilter).map((item2) => {
									return <MenuItem value={item2}>{dateFilter[item2]}</MenuItem>
								})}
						</Select>
					</Grid>
					{!['isNotEmpty', 'isEmpty', 'total'].includes(value.operator) && (
						<Grid item xs md lg>
							<RenderValueCom
								selectedStatusColumn={selectedStatusColumn}
								item={value}
								changeAddFilValue={handleChangeValue}
							/>
						</Grid>
					)}
				</Grid>
			</Grid>
			<Grid item xs={1.5} md={1.5} lg={1.5} sx={{ textAlign: 'right' }}>
				<Button
					aria-label="delete"
					size="small"
					variant="contained"
					sx={{
						borderRadius: '50%',
						width: '25px !important',
						minWidth: '25px !important',
						height: '25px !important',
					}}
					onClick={(e) => handleDeleteItem()}
				>
					<DeleteIcon fontSize="small" />
				</Button>
			</Grid>
		</Grid>
	)
}

const RenderValueCom = (props) => {
	/* type STRING ,operator isAnyOf => textbox multi */

	/* type STRING, operator isEmpty, isNotEmpty => not show values box*/

	/* type DATETIME, operator isEmpty, isNotEmpty => not show values box */

	/* type DATETIME, orther operator => show picker */

	/* type FLOAT, INTEGER, operator isEmpty, isNotEmpty => not show values box */

	/* type FLOAT, INTEGER, other operator => show values number box */

	/* else show textbox */

	//<Input id="component-simple" InputLabelProps={{ shrink: false }} />
	const { iconTheme } = useContext(IconThemeContext)
	const classes = useStyles(iconTheme)
	const { item, changeAddFilValue, selectedStatusColumn } = props
	const isAnyOfOprator = useMemo(() => item.operator === 'isAnyOf', [item])

	const changeValue = (e, valuesParam) => {
		//incase isAnyOf values is an array
		let newValue = e.target.value
		if (isAnyOfOprator && valuesParam) {
			changeAddFilValue([...valuesParam])
		} else {
			if (newValue === item.value) {
				return
			}

			changeAddFilValue(newValue)
		}
	}

	return item.operator === 'isEmpty' || item.operator === 'isNotEmpty' ? (
		<>{/*not show value box*/}</>
	) : (
		<>
			{item.operator === 'isAnyOf' ? (
				<>
					<Autocomplete
						size="small"
						multiple
						options={[]}
						freeSolo
						defaultValue={
							!isAnyOfOprator
								? item.value
								: Array.isArray(item.value)
								? [...item.value]
								: [item.value]
						}
						renderTags={(values, getTagProps) => (
							<Box
								sx={{
									maxHeight: (theme) => theme.spacing(8),
									overflowY: 'auto',
								}}
							>
								{values.map((value, index) => (
									<Chip key={index} variant="standard" label={value} {...getTagProps({ index })} />
								))}
							</Box>
						)}
						onChange={changeValue}
						sx={{
							'& .MuiAutocomplete-inputRoot': {
								height: '3em',
								paddingTop: '1em',
							},
							height: '40px',
						}}
						renderInput={(params) => (
							<TextField
								size="small"
								{...params}
								variant="outlined"
								type={numberTypes?.includes(selectedStatusColumn?.type) ? 'number' : ''}
							/>
						)}
					/>
				</>
			) : (
				<>
					{selectedStatusColumn?.type === 'DATETIME' ? (
						//<LocalizationProvider dateAdapter={DateAdapter} locale={locale}>
						//    <DatePicker />
						//id="map-date"
						//label={"Value"}
						//value={item?.value}
						//onChange={(newValue) => handleDateChange(newValue, false)}

						//</LocalizationProvider>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<DateTimePicker
								//label="With Time Clock
								value={toDayjs(moment.utc(item.value).toDate(), true)}
								onChange={(newValue) => {
									if (!newValue?.isValid()) return
									const newDate = newValue.toISOString()
									changeAddFilValue(newDate)
								}}
								onAccept={(newValue) => {
									if (!newValue?.isValid()) return
									const newDate = newValue.toISOString()
									changeAddFilValue(newDate)
								}}
								views={['year', 'month', 'day', 'hours', 'minutes', 'seconds']}
								timeSteps={{ minutes: 1, seconds: 1 }}
								slotProps={{
									textField: {
										size: 'small',
										variant: 'outlined',
										InputLabelProps: { shrink: true },
										onChange: (e) => {
											if (!e?.isValid()) return
											const newDate = e.toISOString()
											changeAddFilValue(newDate)
										},
									},
								}}
							/>
						</LocalizationProvider>
					) : selectedStatusColumn?.type === 'FLOAT' || selectedStatusColumn?.type === 'INTEGER' ? (
						<TextField
							sx={{
								fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
								fontWeight: '400',
								lineHeight: '1.4375em',
								letterSpacing: '0.00938em',
							}}
							type="number"
							value={item.value}
							onChange={changeValue}
							size="small"
							variant="outlined"
							InputProps={{
								sx: {
									height: '40px',
								},
							}}
						/>
					) : (
						<TextField
							sx={{
								fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
								fontWeight: '400',
								lineHeight: '1.4375em',
								letterSpacing: '0.00938em',
							}}
							id="filter-value"
							value={item.value}
							onChange={changeValue}
							size="small"
							InputProps={{
								sx: {
									height: '40px',
								},
							}}
						/>
					)}
				</>
			)}
		</>
	)
}

export default JobSummaryWidgit
